import {
	isSignedMsgUserOrdersAccountInitializedQueryKey,
	useIsSignedMsgUserOrdersAccountInitialized,
} from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import useDriftClient from 'src/hooks/useDriftClient';
import { initializeSignedMsgUserOrdersAccount } from 'src/utils/signedMsgs';
import { useQueryClient } from '@tanstack/react-query';
import Switch from 'src/components/primitives/Switch/Switch';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { DriftClient } from '@drift-labs/sdk';
import { WithClassName } from 'src/utils/UtilityTypes';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';

interface Props extends WithClassName {
	value: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
}

export const SwiftToggleSwitch = ({
	value,
	onChange,
	disabled,
	className,
}: Props) => {
	const currentUserAccount = useCurrentUserAccount();
	const authority = currentUserAccount?.authority; // use userAccount's authority instead of connected wallet pubkey because this can be a delegate account
	const driftClient = useDriftClient();
	const queryClient = useQueryClient();
	const { authorityWallet, authorityWalletNoFeePayer } = useWalletsV2();

	const { data: isSignedMsgUserOrdersAccountInitialized } =
		useIsSignedMsgUserOrdersAccountInitialized();

	const needsInitialization =
		!isSignedMsgUserOrdersAccountInitialized && !!authority;

	const handleChange = async (newValue: boolean) => {
		// If the user is enabling Swift for the first time they'll need to
		// initialize their account / sign a message.
		if (newValue && needsInitialization) {
			if (!authorityWallet) return;

			const initializedResult = await initializeSignedMsgUserOrdersAccount(
				driftClient as DriftClient,
				authority,
				authorityWallet,
				authorityWalletNoFeePayer
			);

			if (initializedResult) {
				queryClient.invalidateQueries({
					queryKey: isSignedMsgUserOrdersAccountInitializedQueryKey(
						authority.toBase58()
					),
				});

				onChange(true);
			}
			// Otherwise, we can just update directly, regardless of disable / enable.
		} else {
			onChange(newValue);
		}
	};

	return (
		<Switch
			size="sm"
			checked={value}
			onCheckedChange={() => handleChange(!value)}
			disabled={disabled}
			className={className}
		/>
	);
};
